<template>
  <div class="pagination" v-if="totalPages > 1 || showInfo" data-replay-filtre>
    <PaginationInfo
      :show-info="showInfo"
      :show-items-per-page="showItemsPerPage"
      :start-item="startItem"
      :end-item="endItem"
      :total="total"
      :items-per-page="itemsPerPage"
      :items-per-page-options="itemsPerPageOptions"
      @items-per-page-change="handleItemsPerPageChange"
    />

    <!-- Une seule page (ex. « Tout afficher ») : la nav n'a plus d'utilité, mais le
         bloc reste monté pour garder le sélecteur « par page » accessible en bas. -->
    <PaginationNavigation
      v-if="totalPages > 1"
      :current-page="currentPage"
      :total-pages="totalPages"
      :visible-pages="visiblePages"
      :show-first-page="showFirstPage"
      :show-first-ellipsis="showFirstEllipsis"
      :show-last-page="showLastPage"
      :show-last-ellipsis="showLastEllipsis"
      :loading="loading"
      @go-to-page="goToPage"
    />

    <PaginationQuickJump
      v-if="showQuickJump"
      :show-quick-jump="showQuickJump"
      :quick-jump-page="quickJumpPage"
      :total-pages="totalPages"
      :is-valid-quick-jump-page="isValidQuickJumpPage"
      :loading="loading"
      @update:quick-jump-page="quickJumpPage = $event"
      @quick-jump="handleQuickJump"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import PaginationInfo from './pagination/PaginationInfo.vue'
import PaginationNavigation from './pagination/PaginationNavigation.vue'
import PaginationQuickJump from './pagination/PaginationQuickJump.vue'
import { usePaginationComputed } from '../../composables/ui/usePaginationComputed.js'
import { usePaginationActions } from '../../composables/ui/usePaginationActions.js'
import { usePaginationQuickJump } from '../../composables/ui/usePaginationQuickJump.js'
import { PAGE_SIZE_OPTIONS } from '../../composables/useListPreferences.js'

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
    validator: (value) => value > 0
  },
  total: {
    type: Number,
    required: true,
    default: 0
  },
  itemsPerPage: {
    type: Number,
    default: 100
  },
  itemsPerPageOptions: {
    type: Array,
    default: () => PAGE_SIZE_OPTIONS
  },
  showItemsPerPage: {
    type: Boolean,
    default: false
  },
  showInfo: {
    type: Boolean,
    default: true
  },
  showQuickJump: {
    type: Boolean,
    default: false
  },
  maxVisiblePages: {
    type: Number,
    default: 2
  },
  loading: {
    type: Boolean,
    default: false
  },
  // Mode « Tout afficher » : lignes déjà chargées, pour que le compteur
  // n'annonce pas la liste entière alors qu'une seule tranche est à l'écran.
  loadedCount: {
    type: Number,
    default: null
  },
  scrollTarget: {
    type: [Object, null],
    default: null
  }
})

const emit = defineEmits(['update:currentPage', 'update:itemsPerPage', 'page-change', 'items-per-page-change'])

const computedValues = usePaginationComputed(props)
const { totalPages, startItem, endItem, visiblePages, showFirstPage, showFirstEllipsis, showLastPage, showLastEllipsis } = computedValues

const currentPageRef = computed(() => props.currentPage)

const actions = usePaginationActions(props, emit, props.scrollTarget, totalPages)
const { goToPage, handleItemsPerPageChange } = actions

const quickJump = usePaginationQuickJump(currentPageRef, totalPages, goToPage)
const { quickJumpPage, isValidQuickJumpPage, handleQuickJump } = quickJump
</script>

<style scoped>
.pagination {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
  padding: var(--spacing-md);
  align-items: center;
}

@media (max-width: 768px) {
  .pagination {
    padding: var(--spacing-sm);
  }
}
</style>

